export type Alignment = 'left' | 'center' | 'right';
export type FlexAlignment = 'start' | 'center' | 'end';

export type AccentColorsProps = {
  primary: string;
  secondary: string;
  // These are the swatches that the user can add and modify
  additionalColors: Record<string, string>[];
  // These are stored in case someone was still using them before the deletion, the user is not able to modify these anymore once moved to this list
  deletedAdditionalColors: Record<string, string>[];
};

export type NeutralsColorsProps = {
  white: string;
  gray1: string;
  gray2: string;
  gray3: string;
  black: string;
};

export type ColorsProps = {
  system: Omit<AccentColorsProps, 'additionalColors' | 'deletedAdditionalColors'>;
  content: AccentColorsProps;
  neutral: NeutralsColorsProps;
};

export type FontWeight = 'normal' | 500 | 600 | 'bold';

export type FontProps = {
  fontFamily: string;
  fontSize: number;
  fontWeight: FontWeight;
  color: string;
  lineHeight: number;
  letterSpacing: number;
};

export enum ImageFit {
  Cover = 'cover',
  Fill = 'fill',
  Contain = 'contain',
  None = 'none'
}

export type BackgroundRepeat = 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y';
export type BackgroundSize = 'auto' | 'cover' | 'contain';
export type BackgroundPosition =
  | 'left top'
  | 'left center'
  | 'left bottom'
  | 'center top'
  | 'center center'
  | 'center bottom'
  | 'right top'
  | 'right center'
  | 'right bottom';

export type BackgroundProps = {
  backgroundColor: string;
  backgroundImage: string;
  backgroundRepeat: BackgroundRepeat;
  backgroundSize: BackgroundSize;
  backgroundPosition: BackgroundPosition;
};

export type BorderProps = {
  borderColor: string;
  borderWidth: number;
  borderWidthTop: number;
  borderWidthRight: number;
  borderWidthBottom: number;
  borderWidthLeft: number;
  borderStyle: string;
  isSplit: boolean;
};

export type BorderRadiusProps = {
  radius: number;
  topLeft: number;
  topRight: number;
  bottomRight: number;
  bottomLeft: number;
  isSplit: boolean;
};

export type PaddingBlockProps = {
  padding: number;
  top: number;
  bottom: number;
  isSplit: boolean;
};

export type PaddingInlineProps = {
  padding: number;
  left: number;
  right: number;
  isSplit: boolean;
};

export type FullBorderProps = {
  border: BorderProps;
  borderRadius: BorderRadiusProps;
};

export type ShadowType = 'soft' | 'strong' | 'hard' | 'none';

export type ShadowProps = {
  type: ShadowType;
  soft: { value: string; color: string };
  strong: { value: string; color: string };
  hard: { value: string; color: string };
};

export enum ButtonStyle {
  Primary = 'primary',
  Secondary = 'secondary',
  Outlined = 'outlined',
  Text = 'text'
}

export type QuestionButtonProps = {
  style: ButtonStyle;
  borderRadius: BorderRadiusProps;
};

export enum TitleStyle {
  Regular = 'regular',
  SideBorder = 'sideBorder',
  BottomBorder = 'bottomBorder',
  Filled = 'filled'
}

export enum QuestionNumberingStyle {
  BeforeTitleWithDot = 'beforeTitleWithDot',
  BeforeTitleNohDot = 'beforeTitleNoDot',
  AfterTitleNohDot = 'afterTitleNoDot',
  None = 'none'
}

export type QuestionTitleProps = {
  style: TitleStyle;
  color: string;
  numbering: QuestionNumberingStyle;
};

export enum BulletType {
  Number = 'number',
  Letter = 'letter',
  Icon = 'icon',
  None = 'none'
}

export enum BulletStyle {
  CircleFilled = 'circleFilled',
  RoundedSquareFilled = 'roundedSquareFilled',
  SquareFilled = 'squareFilled',
  CircleOutlined = 'circleOutlined',
  RoundedSquareOutlined = 'roundedSquareOutlined',
  SquareOutlined = 'squareOutlined',
  Text = 'text',
  TextWithDot = 'textWithDot'
}

const createIconArray = (count: number) => {
  return Array.from({ length: count }, (_, i) => `bullet${i + 1}`);
};
const iconArray = createIconArray(35);

export type BulletAsIconStyle = (typeof iconArray)[number];

export type BulletProps = {
  type: BulletType;
  color: string;
  number: BulletStyle;
  letter: BulletStyle;
  icon: BulletAsIconStyle;
};

/************* */

export type BorderRadiusThemeProps = {
  topLeft: number;
  topRight: number;
  bottomRight: number;
  bottomLeft: number;
  measureUnit: '1px' | '1%';
};

export type BorderThemeProps = {
  borderColor: string | undefined;
  borderWidth: number;
  borderStyle: string;
};

export type TopImageFit = 'left' | 'right' | 'center' | 'fill';
export type TopImageThemeProps = {
  topImageEnabled: boolean;
  topImageSrc: string;
  topImageMobileSrc: string;
  topImageFit: TopImageFit;
  topImageOverlap: boolean;
  topImageSticky: boolean;
};

export type ColorThemeProps = {
  color: string;
  backgroundColor: string;
};

export type DropShadow = {
  blur: number;
  color: string | undefined;
  offsetX: number;
  offsetY: number;
  size: number;
};

//todo: to be removed after top bar will be handled
export type OldBasicThemeProps = ColorThemeProps & {
  borderColor?: string;
  borderWidth?: number;
  borderStyle?: string;
  borderRadius: number;
  fontFamily?: string;
};

export enum VerticalAlignment {
  top = 'top',
  middle = 'middle',
  bottom = 'bottom'
}

export enum HorizontalAlignment {
  start = 'start',
  center = 'center',
  end = 'end'
}

export enum ImagePosition {
  start = 'start',
  end = 'end'
}

export type PaddingThemeProps = {
  bottom: number;
  inlineEnd: number;
  inlineStart: number;
  top: number;
};

export type ActivityThemeStyles = {
  colors: ColorsProps;
  fonts: {
    title: FontProps;
    headline: FontProps;
    subHeadline: FontProps;
    bodyText: FontProps;
  };
  backgrounds: {
    page: BackgroundProps;
    workingArea: {
      regular: BackgroundProps & FullBorderProps;
      start: BackgroundProps & FullBorderProps;
      end: BackgroundProps & FullBorderProps;
    };
  };
  cardElement: FullBorderProps & {
    backgroundColor: string;
    shadow: ShadowProps;
    blockSpacing: number;
    paddingBlock: PaddingBlockProps;
    paddingInline: PaddingInlineProps;
  };
  questions: {
    wrapAsCard: boolean;
    buttons: {
      main: QuestionButtonProps;
      helper: QuestionButtonProps;
    };
    titles: {
      title: QuestionTitleProps;
      bullet: BulletProps;
    };
  };
  stage: {
    minQuestionInputHeight: string;
    questionInputWrapperPadding: string;
    questionInputWrapperPaddingSum: string;
    navigationButtonFontFamily: string;
  };
  topbar: ColorThemeProps & {
    burger: {
      backgroundColor: string;
    };
    avatar: ColorThemeProps;
    viewModeDropdown: {
      color: string;
    };
    actionIcon: {
      standard: ColorThemeProps;
      disabled: ColorThemeProps;
    };
    saveButton: {
      standard: ColorThemeProps;
      hovered: ColorThemeProps;
      disabled: ColorThemeProps;
    };
    publishButton: {
      standard: ColorThemeProps;
      hovered: ColorThemeProps;
      disabled: ColorThemeProps;
    };
    player: ColorThemeProps & {
      activeMenu: {
        backgroundColor: string;
      };
      loginButton: {
        standard: OldBasicThemeProps;
        hovered: OldBasicThemeProps;
      };
      actionIcon: {
        standard: OldBasicThemeProps;
        hovered: OldBasicThemeProps;
      };
    };
  };
  system: {
    fontFamily: string;
    secondaryFontFamily: string;
  };
};
